import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Play, X, Users, Radio, ChevronRight, Trophy, Crown,
  Timer, Pause, FastForward, Check, Sparkles, LayoutGrid, Trash2,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import { formatEuros } from "../../lib/format";

/* ============================================================================
   Shared helpers
============================================================================ */
const PHASE_LABEL = {
  waiting: "Salle d'attente",
  quiz_intro: "Intro quiz",
  question_active: "Question en cours",
  ranking_intermediate: "Classement",
  quiz_final_ranking: "Classement final quiz",
  event_recap: "Récapitulatif",
  completed: "Terminé",
};

function useCountdown(deadlineIso, paused) {
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!deadlineIso || paused) {
      setRemaining(null);
      return;
    }
    const compute = () => {
      const now = Date.now();
      const dl = new Date(deadlineIso).getTime();
      setRemaining(Math.max(0, Math.ceil((dl - now) / 1000)));
    };
    compute();
    const id = setInterval(compute, 250);
    return () => clearInterval(id);
  }, [deadlineIso, paused]);
  return remaining;
}

/* ============================================================================
   ADMIN — Live Events list
   Route: /admin/live-events
============================================================================ */
export default function AdminLiveEvents() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/live-events");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/admin")} data-testid="live-back-admin"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour Admin
        </button>
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black flex items-center gap-3">
              <Radio className="w-8 h-8 text-fuchsia-400"/> Soirées Live
            </h1>
            <p className="text-sm text-white/60 mt-1">Multi-quiz, animateur en salle et écran géant.</p>
          </div>
          <button data-testid="live-create-btn" onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase tracking-widest">
            <Plus className="w-4 h-4"/> Créer une soirée
          </button>
        </div>
        <div className="grid gap-3">
          {items.length === 0 ? (
            <div className="text-white/50 text-sm">Aucune soirée pour le moment.</div>
          ) : items.map(ev => (
            <div key={ev.id} data-testid={`live-item-${ev.id}`}
              className="panel rounded-2xl border border-white/10 p-4 sm:p-5 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-lg">{ev.title}</span>
                  <span data-testid={`live-item-${ev.id}-code`}
                    className="font-mono-stat text-xs uppercase tracking-widest text-cyan-300 bg-cyan-400/10 border border-cyan-400/30 px-2 py-0.5 rounded">
                    #{ev.code}
                  </span>
                  <StatusPill status={ev.status}/>
                </div>
                <div className="text-xs text-white/60 mt-1 font-mono-stat flex items-center gap-2 flex-wrap">
                  {ev.place && <span>{ev.place}</span>}
                  <span>·</span>
                  <span>{(ev.quizzes || []).length} quiz</span>
                  <span>·</span>
                  <span>{(ev.quizzes || []).reduce((s, q) => s + (q.questions?.length || 0), 0)} questions</span>
                  {typeof ev.price_amount === "number" && <><span>·</span><span>{formatEuros(ev.price_amount)} entrée</span></>}
                </div>
              </div>
              <button onClick={() => navigate(`/admin/live-events/${ev.id}`)}
                data-testid={`live-item-${ev.id}-open`}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-mono-stat uppercase tracking-widest">
                Ouvrir la console
              </button>
            </div>
          ))}
        </div>
      </div>
      {showCreate && (
        <CreateEventDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </Layout>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: ["bg-white/10 border-white/20 text-white/70", "Brouillon"],
    open: ["bg-cyan-400/20 border-cyan-400/40 text-cyan-200", "Ouverte"],
    running: ["bg-fuchsia-500/20 border-fuchsia-400/40 text-fuchsia-200", "En cours"],
    completed: ["bg-emerald-500/20 border-emerald-400/40 text-emerald-200", "Terminée"],
  };
  const [cls, label] = map[status] || map.draft;
  return <span className={`text-[10px] uppercase tracking-widest font-mono-stat px-2 py-0.5 rounded border ${cls}`}>{label}</span>;
}

/* ============================================================================
   CreateEventDialog — multi-quiz builder
============================================================================ */
const _emptyOptions = () => [{ id: "a", text: "" }, { id: "b", text: "" }, { id: "c", text: "" }, { id: "d", text: "" }];
// Compact short IDs — must stay ≤ 64 chars server-side and unique per event.
let _idCounter = 0;
const _shortId = (prefix) => `${prefix}${(++_idCounter).toString(36)}${Date.now().toString(36).slice(-4)}`;
const _emptyQuestion = () => ({
  id: _shortId("q"),
  text: "",
  options: _emptyOptions(),
  correct_option_id: "a",
});
const _emptyQuiz = (idx) => ({
  id: _shortId("qz"),
  title: `Quiz ${idx + 1}`,
  description: "",
  prize_pool_amount: "",
  default_question_time_seconds: 20,
  default_question_points: 1000,
  questions: [_emptyQuestion(), _emptyQuestion()],
});

function CreateEventDialog({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [place, setPlace] = useState("");
  const [price, setPrice] = useState("");
  const [rewards, setRewards] = useState("");
  const [rankingSeconds, setRankingSeconds] = useState(10);
  const [quizzes, setQuizzes] = useState([_emptyQuiz(0)]);
  const [busy, setBusy] = useState(false);

  const addQuiz = () => setQuizzes(qs => [...qs, _emptyQuiz(qs.length)]);
  const removeQuiz = (i) => setQuizzes(qs => qs.length > 1 ? qs.filter((_, idx) => idx !== i) : qs);
  const moveQuiz = (i, dir) => setQuizzes(qs => {
    const j = i + dir;
    if (j < 0 || j >= qs.length) return qs;
    const copy = [...qs];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const patchQuiz = (i, patch) => setQuizzes(qs => qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const patchQuestion = (qi, questionIdx, patch) => setQuizzes(qs => qs.map((q, idx) => idx !== qi ? q : {
    ...q,
    questions: q.questions.map((question, qIdx) => qIdx === questionIdx ? { ...question, ...patch } : question),
  }));
  const patchOption = (qi, questionIdx, oi, text) => setQuizzes(qs => qs.map((q, idx) => idx !== qi ? q : {
    ...q,
    questions: q.questions.map((question, qIdx) => qIdx !== questionIdx ? question : {
      ...question,
      options: question.options.map((o, oIdx) => oIdx === oi ? { ...o, text } : o),
    }),
  }));
  const addQuestion = (qi) => setQuizzes(qs => qs.map((q, idx) => idx !== qi ? q : {
    ...q, questions: [...q.questions, _emptyQuestion()],
  }));
  const removeQuestion = (qi, questionIdx) => setQuizzes(qs => qs.map((q, idx) => idx !== qi ? q : {
    ...q,
    questions: q.questions.length > 1 ? q.questions.filter((_, i) => i !== questionIdx) : q.questions,
  }));

  const canSubmit = title.trim().length >= 3 && quizzes.every(q =>
    q.title.trim().length >= 2 && q.questions.length > 0 && q.questions.every(question =>
      question.text.trim() && question.options.every(o => o.text.trim())
    )
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Complète toutes les questions et réponses avant de créer."); return; }
    setBusy(true);
    try {
      const body = {
        title, place: place || null,
        price_amount: price ? parseFloat(price) : null,
        rewards_description: rewards || null,
        ranking_display_seconds: Number(rankingSeconds) || 10,
        quizzes: quizzes.map((q, qi) => ({
          id: q.id,
          title: q.title.trim(),
          description: q.description?.trim() || null,
          prize_pool_amount: q.prize_pool_amount === "" ? null : parseFloat(q.prize_pool_amount),
          default_question_time_seconds: Number(q.default_question_time_seconds) || 20,
          default_question_points: Number(q.default_question_points) || 1000,
          questions: q.questions.map((question, questionIdx) => ({
            id: question.id || `q${qi}_${questionIdx}`,
            text: question.text.trim(),
            options: question.options.map(o => ({ id: o.id, text: o.text.trim() })),
            correct_option_id: question.correct_option_id,
          })),
        })),
      };
      await api.post("/admin/live-events", body);
      toast.success("Événement créé");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} data-testid="live-create-form"
        className="w-full max-w-3xl panel rounded-2xl border border-white/10 p-6 space-y-4 relative my-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 p-1 text-white/50 hover:text-white"><X className="w-4 h-4"/></button>
        <h2 className="font-display text-2xl font-bold">Nouvelle soirée Live</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nom de la soirée * (max 80)">
            <input data-testid="live-create-title" required value={title} onChange={e => setTitle(e.target.value)} maxLength={80} minLength={3}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60" placeholder="Soirée Quiz OTRA — Perpignan"/>
          </Field>
          <Field label="Lieu (max 100)">
            <input data-testid="live-create-place" value={place} onChange={e => setPlace(e.target.value)} maxLength={100}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60" placeholder="Bar Le Central"/>
          </Field>
          <Field label="Prix d'entrée (€)">
            <input data-testid="live-create-price" type="number" step="0.5" min="0" value={price} onChange={e => setPrice(e.target.value)}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60" placeholder="20"/>
          </Field>
          <Field label="Durée classement (s) — entre 2 questions">
            <input data-testid="live-create-ranking-seconds" type="number" min="3" max="60" value={rankingSeconds} onChange={e => setRankingSeconds(e.target.value)}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60"/>
          </Field>
          <Field label="Récompenses (résumé, max 300)" className="sm:col-span-2">
            <textarea data-testid="live-create-rewards" value={rewards} onChange={e => setRewards(e.target.value)} rows={2} maxLength={300}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60" placeholder="Cagnottes par quiz : 100€ · 100€ · 100€"/>
          </Field>
        </div>

        <div className="pt-3 border-t border-white/10">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-mono-stat uppercase tracking-widest text-white/70">Quiz de la soirée ({quizzes.length})</div>
            <button type="button" data-testid="live-create-add-quiz" onClick={addQuiz}
              className="px-3 py-1.5 rounded-lg bg-fuchsia-400/20 border border-fuchsia-400/40 text-fuchsia-200 text-xs uppercase tracking-widest font-mono-stat hover:bg-fuchsia-400/30">
              + Ajouter un quiz
            </button>
          </div>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {quizzes.map((quiz, qi) => (
              <div key={qi} data-testid={`live-create-quiz-${qi}`} className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/5 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-mono-stat">Quiz {qi + 1}</span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => moveQuiz(qi, -1)} disabled={qi === 0}
                      className="p-1 text-white/50 hover:text-white disabled:opacity-30" title="Monter">▲</button>
                    <button type="button" onClick={() => moveQuiz(qi, +1)} disabled={qi === quizzes.length - 1}
                      className="p-1 text-white/50 hover:text-white disabled:opacity-30" title="Descendre">▼</button>
                    {quizzes.length > 1 && (
                      <button type="button" data-testid={`live-create-remove-quiz-${qi}`} onClick={() => removeQuiz(qi)}
                        className="p-1 text-white/50 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5"/></button>
                    )}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <input data-testid={`live-create-quiz-${qi}-title`} required value={quiz.title}
                    onChange={e => patchQuiz(qi, { title: e.target.value })} maxLength={80} minLength={2} placeholder="Titre du quiz (max 80)"
                    className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60 text-sm"/>
                  <input data-testid={`live-create-quiz-${qi}-prize`} type="number" step="1" min="0" value={quiz.prize_pool_amount}
                    onChange={e => patchQuiz(qi, { prize_pool_amount: e.target.value })} placeholder="Cagnotte (€, optionnel)"
                    className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60 text-sm"/>
                </div>
                <textarea data-testid={`live-create-quiz-${qi}-description`} value={quiz.description}
                  onChange={e => patchQuiz(qi, { description: e.target.value })} rows={1} maxLength={200}
                  placeholder="Description courte (optionnel, max 200)"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60 text-sm"/>
                {/* Quiz-level defaults applied to every question of this quiz */}
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm">
                    <span className="text-[10px] uppercase tracking-widest text-white/50 font-mono-stat">Temps / question</span>
                    <input data-testid={`live-create-quiz-${qi}-default-time`} type="number" min="5" max="120"
                      value={quiz.default_question_time_seconds}
                      onChange={e => patchQuiz(qi, { default_question_time_seconds: Number(e.target.value) || 20 })}
                      className="ml-auto w-14 bg-transparent outline-none text-right font-mono-stat font-bold"/>
                    <span className="text-xs text-white/40">s</span>
                  </label>
                  <label className="flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm">
                    <span className="text-[10px] uppercase tracking-widest text-white/50 font-mono-stat">Points / question</span>
                    <input data-testid={`live-create-quiz-${qi}-default-points`} type="number" min="100" max="5000" step="100"
                      value={quiz.default_question_points}
                      onChange={e => patchQuiz(qi, { default_question_points: Number(e.target.value) || 1000 })}
                      className="ml-auto w-20 bg-transparent outline-none text-right font-mono-stat font-bold"/>
                  </label>
                </div>

                {/* Questions of this quiz */}
                <div className="pt-2 border-t border-white/5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-cyan-300 font-mono-stat">Questions ({quiz.questions.length}) — {quiz.default_question_time_seconds}s · {quiz.default_question_points} pts</span>
                    <button type="button" data-testid={`live-create-quiz-${qi}-add-question`} onClick={() => addQuestion(qi)}
                      className="px-2 py-1 rounded bg-cyan-400/20 border border-cyan-400/40 text-cyan-200 text-[10px] uppercase font-mono-stat hover:bg-cyan-400/30">
                      + Question
                    </button>
                  </div>
                  {quiz.questions.map((question, questionIdx) => (
                    <div key={questionIdx} data-testid={`live-create-quiz-${qi}-question-${questionIdx}`}
                      className="rounded-lg bg-black/40 border border-white/5 p-2 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-widest text-white/40 font-mono-stat">Q {questionIdx + 1}</span>
                        {quiz.questions.length > 1 && (
                          <button type="button" onClick={() => removeQuestion(qi, questionIdx)}
                            className="text-white/40 hover:text-rose-300 text-[10px]">Supprimer</button>
                        )}
                      </div>
                      <input data-testid={`live-create-quiz-${qi}-question-${questionIdx}-text`} required value={question.text}
                        onChange={e => patchQuestion(qi, questionIdx, { text: e.target.value })} maxLength={500}
                        placeholder="Texte de la question"
                        className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 outline-none focus:border-cyan-400/60 text-sm"/>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {question.options.map((o, oi) => (
                          <label key={oi} className={`flex items-center gap-2 rounded border px-2 py-1 cursor-pointer transition ${question.correct_option_id === o.id ? "border-emerald-400/60 bg-emerald-400/10" : "border-white/10 bg-black/40"}`}>
                            <input type="radio" name={`q-${qi}-${questionIdx}-correct`} checked={question.correct_option_id === o.id}
                              onChange={() => patchQuestion(qi, questionIdx, { correct_option_id: o.id })}
                              data-testid={`live-create-quiz-${qi}-question-${questionIdx}-correct-${oi}`}
                              className="accent-emerald-400"/>
                            <span className="text-[10px] font-mono-stat text-white/50 w-3">{String.fromCharCode(65 + oi)}.</span>
                            <input data-testid={`live-create-quiz-${qi}-question-${questionIdx}-opt-${oi}`} required value={o.text}
                              onChange={e => patchOption(qi, questionIdx, oi, e.target.value)} maxLength={200}
                              placeholder={`Réponse ${String.fromCharCode(65 + oi)}`}
                              className="flex-1 bg-transparent outline-none text-sm"/>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button data-testid="live-create-submit" disabled={busy || !canSubmit}
          className="w-full py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase tracking-widest disabled:opacity-50">
          {busy ? "Création..." : "Créer la soirée"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] uppercase tracking-widest text-white/50 font-mono-stat">{label}</span>
      {children}
    </label>
  );
}

/* ============================================================================
   ADMIN — Animator Console
   Route: /admin/live-events/:id
============================================================================ */
export function AdminLiveConsole() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/live-events/${id}`);
      setEv(data);
      const parts = await api.get(`/admin/live-events/${id}/participants`);
      setParticipants(parts.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [load]);

  const call = async (path, opts = {}) => {
    setBusy(true);
    try { await api.post(`/admin/live-events/${id}/${path}`); await load(); if (opts.msg) toast.success(opts.msg); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!ev) return <Layout><div className="max-w-6xl mx-auto px-4 py-8 text-white/50">Chargement…</div></Layout>;

  const phase = ev.phase;
  const canNext = ["waiting", "quiz_intro", "quiz_final_ranking", "event_recap"].includes(phase);
  const canPause = ["question_active", "ranking_intermediate"].includes(phase);
  const nextLabel = {
    waiting: "Démarrer la soirée",
    quiz_intro: "Lancer la 1ère question",
    quiz_final_ranking: ev.current_quiz_index + 1 < (ev.quizzes || []).length ? "Quiz suivant" : "Aller au récap",
    event_recap: "Clôturer la soirée",
  }[phase] || "Suivant";

  const publicJoin = `${window.location.origin}/live/${ev.code}`;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button onClick={() => navigate("/admin/live-events")} data-testid="console-back"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition mb-4">
          <ArrowLeft className="w-4 h-4"/> Retour Live Events
        </button>
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 data-testid="console-title" className="font-display text-2xl sm:text-3xl font-black">{ev.title}</h1>
            <div className="text-xs text-white/60 mt-1 font-mono-stat flex items-center gap-2 flex-wrap">
              <span data-testid="console-code">Code: #{ev.code}</span>
              <span>·</span>
              <StatusPill status={ev.status}/>
              <span>·</span>
              <span data-testid="console-phase">Phase: {PHASE_LABEL[phase] || phase}</span>
              <span>·</span>
              <span data-testid="console-participants-count">{participants.filter(p => !p.kicked).length} joueurs</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a data-testid="console-join-url" href={publicJoin} target="_blank" rel="noreferrer"
              className="text-xs font-mono-stat text-cyan-300 hover:underline">{publicJoin}</a>
            <button data-testid="console-open-screen" onClick={() => window.open(`/live/${ev.code}/screen`, "_blank")}
              className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-mono-stat uppercase">
              <Radio className="w-3.5 h-3.5 inline mr-1"/> Écran géant
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          {/* Main console */}
          <div className="panel rounded-2xl border border-white/10 p-4 sm:p-6 space-y-5">
            <PhaseBanner ev={ev} />
            <QuizProgress ev={ev} />

            <div className="flex items-center gap-2 flex-wrap">
              {ev.status === "draft" && (
                <button data-testid="ctrl-publish" disabled={busy} onClick={() => call("publish", { msg: "Événement publié" })}
                  className="px-5 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-widest hover:bg-cyan-300 disabled:opacity-50">
                  Publier l&apos;événement
                </button>
              )}
              {canNext && ev.status !== "draft" && (
                <button data-testid="ctrl-next" disabled={busy} onClick={() => call("next")}
                  className="px-6 py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
                  <ChevronRight className="w-4 h-4"/> {nextLabel}
                </button>
              )}
              {canPause && (
                <>
                  <button data-testid="ctrl-pause" disabled={busy} onClick={() => call("pause")}
                    className="px-4 py-3 rounded-xl bg-amber-400/20 border border-amber-400/40 text-amber-200 font-mono-stat uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-50">
                    <Pause className="w-3.5 h-3.5"/> {ev.paused ? "Reprendre" : "Pause"}
                  </button>
                  <button data-testid="ctrl-force-next" disabled={busy} onClick={() => call("force-next")}
                    className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-mono-stat uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-50">
                    <FastForward className="w-3.5 h-3.5"/> Forcer suivant
                  </button>
                </>
              )}
              {phase === "quiz_final_ranking" && ev.current_quiz_index + 1 >= (ev.quizzes || []).length && (
                <button data-testid="ctrl-recap" disabled={busy} onClick={() => call("recap")}
                  className="px-4 py-3 rounded-xl bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 font-mono-stat uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-50">
                  <LayoutGrid className="w-3.5 h-3.5"/> Récapitulatif
                </button>
              )}
              {(phase === "event_recap" || phase === "quiz_final_ranking") && (
                <button data-testid="ctrl-finish" disabled={busy} onClick={() => call("finish", { msg: "Événement clôturé" })}
                  className="px-4 py-3 rounded-xl bg-rose-500/20 border border-rose-400/40 text-rose-200 font-mono-stat uppercase tracking-widest text-xs inline-flex items-center gap-2 disabled:opacity-50">
                  <Check className="w-3.5 h-3.5"/> Clôturer
                </button>
              )}
            </div>

            <ConsoleContent ev={ev} />
          </div>

          {/* Participants */}
          <div className="panel rounded-2xl border border-white/10 p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-white/50 font-mono-stat flex items-center gap-2">
              <Users className="w-4 h-4"/> Participants ({participants.filter(p => !p.kicked).length})
            </div>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {participants.length === 0 ? <div className="text-xs text-white/50">Aucun joueur.</div> : participants.map(p => (
                <div key={p.id} className={`flex items-center justify-between gap-2 text-sm ${p.kicked ? "opacity-40 line-through" : ""}`}>
                  <span>{p.nickname}</span>
                  {!p.kicked && (
                    <button className="text-xs text-white/40 hover:text-rose-300"
                      onClick={async () => {
                        try { await api.post(`/admin/live-events/${id}/participants/${p.id}/kick`); load(); }
                        catch (e) { toast.error(formatApiError(e)); }
                      }}>Kick</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function PhaseBanner({ ev }) {
  const deadlineISO = ev.paused ? null : (ev.phase_deadline || null);
  const remaining = useCountdown(deadlineISO, ev.paused);
  const currentQuiz = (ev.quizzes || [])[ev.current_quiz_index];
  return (
    <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/10 p-4">
      <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-mono-stat mb-1">Phase actuelle</div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-2xl font-display font-bold">{PHASE_LABEL[ev.phase] || ev.phase}</div>
        {remaining !== null && (
          <div className="text-xl font-mono-stat text-amber-300 inline-flex items-center gap-2">
            <Timer className="w-5 h-5"/> {remaining}s
          </div>
        )}
        {ev.paused && <span className="text-xs font-mono-stat text-amber-300 uppercase tracking-widest">⏸ EN PAUSE</span>}
      </div>
      {currentQuiz && ["quiz_intro", "question_active", "ranking_intermediate", "quiz_final_ranking"].includes(ev.phase) && (
        <div className="text-sm text-white/70 mt-2">
          Quiz {ev.current_quiz_index + 1} / {(ev.quizzes || []).length} : <span className="text-white">{currentQuiz.title}</span>
        </div>
      )}
    </div>
  );
}

function QuizProgress({ ev }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {(ev.quizzes || []).map((q, i) => (
        <div key={q.id || i}
          className={`px-3 py-1.5 rounded-lg text-xs font-mono-stat border ${i === ev.current_quiz_index && ev.phase !== "completed"
            ? "bg-fuchsia-500/20 border-fuchsia-400/40 text-fuchsia-200"
            : i < ev.current_quiz_index || (i === ev.current_quiz_index && ["quiz_final_ranking", "event_recap", "completed"].includes(ev.phase))
              ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-200"
              : "bg-white/5 border-white/10 text-white/50"}`}>
          Q{i + 1}
        </div>
      ))}
    </div>
  );
}

function ConsoleContent({ ev }) {
  const currentQuiz = (ev.quizzes || [])[ev.current_quiz_index];
  if (!currentQuiz) return null;
  if (ev.phase === "waiting") {
    return <div className="text-white/60 text-sm">L&apos;événement est prêt. Clique sur <b>Démarrer la soirée</b> quand tu veux lancer.</div>;
  }
  if (ev.phase === "quiz_intro") {
    return (
      <div className="rounded-xl bg-black/30 border border-white/10 p-4">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-mono-stat">Écran d&apos;intro (visible chez les joueurs & écran géant)</div>
        <div className="mt-2 font-display text-2xl font-bold">{currentQuiz.title}</div>
        {currentQuiz.description && <div className="text-sm text-white/70 mt-1">{currentQuiz.description}</div>}
        <div className="mt-2 text-xs font-mono-stat text-white/60">{(currentQuiz.questions || []).length} questions</div>
        {typeof currentQuiz.prize_pool_amount === "number" && (
          <div className="mt-1 text-sm text-amber-300 font-mono-stat">Cagnotte : {formatEuros(currentQuiz.prize_pool_amount)}</div>
        )}
      </div>
    );
  }
  if (["question_active", "ranking_intermediate"].includes(ev.phase)) {
    const q = (currentQuiz.questions || [])[ev.current_question_index || 0];
    if (!q) return null;
    return (
      <div className="rounded-xl bg-black/30 border border-white/10 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-mono-stat">Question {(ev.current_question_index || 0) + 1} / {(currentQuiz.questions || []).length}</div>
        <div className="font-display text-xl font-bold">{q.text}</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {(q.options || []).map(o => {
            const isCorrect = ev.phase === "ranking_intermediate" && o.id === q.correct_option_id;
            return (
              <div key={o.id} className={`px-3 py-2 rounded-lg font-mono-stat text-sm ${isCorrect ? "bg-emerald-500/30 border border-emerald-400 text-emerald-100" : "bg-white/5 border border-white/10 text-white/80"}`}>
                <span className="text-[10px] uppercase tracking-widest text-white/40 mr-2">{o.id.toUpperCase()}.</span>
                {o.text}
                {o.id === q.correct_option_id && <span className="text-[10px] uppercase text-emerald-300 ml-2">bonne rép.</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  if (ev.phase === "quiz_final_ranking") {
    return <div className="text-white/60 text-sm">Classement final du quiz affiché sur l&apos;écran. Clique <b>Suivant</b> pour passer au quiz suivant, ou <b>Récapitulatif</b> pour tout revoir.</div>;
  }
  if (ev.phase === "event_recap") {
    return <div className="text-white/60 text-sm">Récapitulatif complet affiché sur l&apos;écran. Clique <b>Clôturer</b> pour finaliser la soirée.</div>;
  }
  if (ev.phase === "completed") {
    return <div className="text-emerald-300 text-sm font-mono-stat">Soirée clôturée. Consulte les résultats dans le récapitulatif.</div>;
  }
  return null;
}

/* ============================================================================
   PUBLIC — Landing page for players (enter code + nickname)
   Route: /live/:code (optional code param)
============================================================================ */
export function LiveJoin() {
  const navigate = useNavigate();
  const { code: paramCode } = useParams();
  const [code, setCode] = useState((paramCode || "").toUpperCase());
  const [nickname, setNickname] = useState("");
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Client-side idempotency for guest flows: if this browser already
  // registered as a participant for this event, jump straight to /play
  // instead of showing the join form again.
  useEffect(() => {
    const pid = paramCode ? _readPid(paramCode.toUpperCase()) : null;
    if (pid && paramCode) {
      navigate(`/live/${paramCode.toUpperCase()}/play`, { replace: true });
    }
  }, [paramCode, navigate]);

  useEffect(() => {
    if (!code || code.length < 4) { setInfo(null); setInfoError(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/live/${code}/info`);
        if (!cancelled) { setInfo(data); setInfoError(null); }
      } catch (err) {
        if (!cancelled) {
          setInfo(null);
          const status = err?.response?.status;
          if (status === 404) setInfoError("L'événement est introuvable. Vérifie le code.");
          else setInfoError(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/live/${code}/join`, { nickname: nickname.trim() });
      _writeIdentity(code, data.participant_id, data.nickname);
      navigate(`/live/${code}/play`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-fuchsia-950/40 to-cyan-950/40 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md space-y-5">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 font-display font-black text-4xl">
            <Sparkles className="w-8 h-8 text-fuchsia-400"/> OTRAQUIZZ
          </div>
          <div className="text-white/60 text-sm mt-1">Rejoins la soirée en salle</div>
        </div>
        <form onSubmit={submit} className="panel rounded-2xl border border-white/10 p-5 space-y-4">
          <Field label="Code de la soirée">
            <input data-testid="live-join-code" required value={code} onChange={e => setCode(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="ABC123" maxLength={6}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-3 outline-none focus:border-fuchsia-400/60 font-mono-stat text-2xl tracking-widest text-center uppercase"/>
          </Field>
          {info && (
            <div data-testid="live-join-info" className="text-center text-sm space-y-1">
              <div className="font-display font-bold text-lg">{info.title}</div>
              {info.place && <div className="text-white/60">{info.place}</div>}
              <div className="text-xs font-mono-stat text-white/50">{info.quizzes_count} quiz · {info.questions_count} questions</div>
            </div>
          )}
          {infoError && (
            <div data-testid="live-join-error" className="text-center text-sm text-rose-300 font-mono-stat border border-rose-400/40 bg-rose-500/10 rounded-lg py-2 px-3">
              {infoError}
            </div>
          )}
          <Field label="Ton pseudo">
            <input data-testid="live-join-nickname" required value={nickname} onChange={e => setNickname(e.target.value.slice(0, 30))}
              placeholder="Ex : Jerome80" minLength={2}
              disabled={!!infoError}
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-fuchsia-400/60 disabled:opacity-40"/>
          </Field>
          <button data-testid="live-join-submit" type="submit" disabled={busy || !code || nickname.trim().length < 2 || !!infoError}
            className="w-full py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase tracking-widest disabled:opacity-50">
            {busy ? "..." : "Rejoindre"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ============================================================================
   PUBLIC — Player mobile view
   Route: /live/:code/play
============================================================================ */
// Small helper: store/read participant ID from BOTH sessionStorage (fast,
// per-tab) and localStorage (survives browser restarts + Safari ITP quirks).
function _readPid(code) {
  return sessionStorage.getItem(`live_pid_${code}`) || localStorage.getItem(`live_pid_${code}`);
}
function _readNick(code) {
  return sessionStorage.getItem(`live_nick_${code}`) || localStorage.getItem(`live_nick_${code}`);
}
function _writeIdentity(code, pid, nick) {
  try {
    sessionStorage.setItem(`live_pid_${code}`, pid);
    sessionStorage.setItem(`live_nick_${code}`, nick);
  } catch (_) { /* ignore quota */ }
  try {
    localStorage.setItem(`live_pid_${code}`, pid);
    localStorage.setItem(`live_nick_${code}`, nick);
  } catch (_) { /* ignore quota */ }
}

/** Error boundary — captures ANY render crash inside the Live Play tree so
 *  the user sees an actionable French message instead of the notorious
 *  black screen. `resetKey` forces a remount when it changes (e.g. code). */
class LivePlayErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("[LivePlay] Render crash", error, info);
  }
  componentDidUpdate(prev) {
    if (prev.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="play-error" className="min-h-screen bg-gradient-to-br from-black via-rose-950/40 to-black flex flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="font-display font-black text-2xl">Une erreur est survenue</div>
          <div className="text-white/70 max-w-md">Impossible d&apos;afficher l&apos;écran de jeu. Réactualise la page pour retrouver ta place dans la soirée — tu ne perdras pas ta participation.</div>
          <button onClick={() => window.location.reload()}
            className="mt-2 px-5 py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-display font-bold uppercase tracking-widest">
            Actualiser
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function LivePlay() {
  return <LivePlayInner />;
}

function LivePlayInner() {
  const { code } = useParams();
  const navigate = useNavigate();
  const pid = useMemo(() => _readPid(code), [code]);
  const nick = useMemo(() => _readNick(code), [code]);
  const [state, setState] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pollError, setPollError] = useState(null);
  const shownAtRef = useRef({ key: null, at: 0 });

  useEffect(() => {
    if (!pid) { navigate(`/live/${code}`); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/${code}/state?participant_id=${pid}`);
        if (!cancelled) {
          setState(data);
          setPollError(null);
          if (data.phase === "question_active" && data.current_question) {
            const key = `${data.current_quiz_index}-${data.current_question_index}`;
            if (shownAtRef.current.key !== key) {
              shownAtRef.current = { key, at: Date.now() };
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          if (status === 404) {
            setPollError({ fatal: true, msg: "L'événement est introuvable ou a été supprimé." });
          } else {
            setPollError({ fatal: false, msg: "Connexion perdue, reconnexion…" });
          }
        }
      }
    };
    poll();
    const t = setInterval(poll, 1500);
    return () => { cancelled = true; clearInterval(t); };
  }, [code, pid, navigate]);

  const submitAnswer = async (optionId) => {
    if (!state?.current_question || submitting) return;
    setSubmitting(true);
    const shown = shownAtRef.current?.at || Date.now();
    const rt = Math.max(0, Date.now() - shown);
    try {
      await api.post(`/live/${code}/answer`, {
        participant_id: pid,
        quiz_index: state.current_quiz_index,
        question_index: state.current_question_index,
        option_id: optionId,
        response_time_ms: rt,
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  // Fatal error → dedicated screen (no black-out)
  if (pollError?.fatal) {
    return (
      <div data-testid="play-fatal" className="min-h-screen bg-gradient-to-br from-black via-rose-950/40 to-black flex flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="font-display font-black text-2xl">{pollError.msg}</div>
        <button onClick={() => navigate("/live")}
          className="mt-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-mono-stat uppercase tracking-widest text-sm">
          Retour à l&apos;accueil Live
        </button>
      </div>
    );
  }

  // Initial load → colored loading screen (no black)
  if (!state) {
    return (
      <div data-testid="play-loading" className="min-h-screen bg-gradient-to-br from-black via-fuchsia-950/40 to-cyan-950/40 flex flex-col items-center justify-center gap-4 p-6 text-center">
        <Sparkles className="w-12 h-12 text-fuchsia-400 animate-pulse"/>
        <div className="font-display font-bold text-xl">Connexion à la soirée…</div>
        {pollError && !pollError.fatal && (
          <div className="text-amber-300 font-mono-stat text-sm">{pollError.msg}</div>
        )}
      </div>
    );
  }

  return (
    <LivePlayErrorBoundary resetKey={code}>
      <div className="min-h-screen bg-gradient-to-br from-black via-fuchsia-950/40 to-cyan-950/40 flex flex-col p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="font-display font-bold flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-fuchsia-400"/> OTRAQUIZZ
          </div>
          <div className="text-right">
            <div data-testid="play-nickname" className="text-xs font-mono-stat text-cyan-300">{nick}</div>
            {state.my_score != null && <div className="text-[10px] font-mono-stat text-white/50">Total quiz : {state.my_score} pts</div>}
          </div>
        </div>
        {state.paused && (
          <div data-testid="play-paused-badge" className="text-center text-xs font-mono-stat text-amber-300 uppercase tracking-widest mb-2 border border-amber-400/40 bg-amber-500/10 rounded-lg py-1.5">
            ⏸ Pause — l&apos;animateur reprend bientôt
          </div>
        )}
        {pollError && !pollError.fatal && (
          <div data-testid="play-reconnecting" className="text-center text-xs font-mono-stat text-amber-300 uppercase tracking-widest mb-2 border border-amber-400/40 bg-amber-500/10 rounded-lg py-1.5">
            🔄 {pollError.msg}
          </div>
        )}

        <PlayContent state={state} onSubmit={submitAnswer} submitting={submitting} />
      </div>
    </LivePlayErrorBoundary>
  );
}

function PlayContent({ state, onSubmit, submitting }) {
  const remaining = useCountdown(state.paused ? null : state.phase_deadline, state.paused);
  const phase = state.phase;
  if (phase === "waiting") {
    return (
      <div data-testid="play-waiting" className="flex-1 flex flex-col items-center justify-center text-center gap-3">
        <Radio className="w-12 h-12 text-fuchsia-400 animate-pulse"/>
        <div className="font-display text-2xl font-bold">{state.title}</div>
        <div className="text-white/70 text-lg">L&apos;événement va bientôt commencer</div>
        <div className="text-white/40 text-sm">Installe-toi, le quiz démarre bientôt</div>
      </div>
    );
  }
  if (phase === "quiz_intro" && state.current_quiz) {
    return (
      <div data-testid="play-quiz-intro" className="flex-1 flex flex-col items-center justify-center text-center gap-3 px-4">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-mono-stat">Quiz {state.current_quiz_index + 1} / {state.quizzes_count}</div>
        <div className="font-display text-3xl font-black">{state.current_quiz.title}</div>
        {state.current_quiz.description && <div className="text-white/70">{state.current_quiz.description}</div>}
        <div className="mt-2 text-white/80 font-mono-stat">{state.current_quiz.questions_count} questions</div>
        {typeof state.current_quiz.prize_pool_amount === "number" && (
          <div className="text-xl text-amber-300 font-mono-stat">Cagnotte : {formatEuros(state.current_quiz.prize_pool_amount)}</div>
        )}
        <div className="mt-4 text-fuchsia-300 uppercase font-mono-stat tracking-widest text-sm">Préparez-vous</div>
      </div>
    );
  }
  if (phase === "question_active" && state.current_question) {
    const q = state.current_question;
    const answered = !!state.my_answer;
    return (
      <div className="flex-1 flex flex-col gap-3">
        <div className="flex items-center justify-between text-xs font-mono-stat text-white/50">
          <span>Q {state.current_question_index + 1} / {state.current_quiz?.questions_count}</span>
          {remaining !== null && <span data-testid="play-timer" className="text-amber-300 text-lg inline-flex items-center gap-1"><Timer className="w-4 h-4"/> {remaining}s</span>}
        </div>
        <div data-testid="play-question-text" className="font-display text-xl font-bold text-center px-3 break-words">{q.text}</div>
        <div className="grid grid-cols-1 gap-2 mt-2">
          {(q.options || []).map((o, i) => {
            const isMine = answered && state.my_answer?.option_id === o.id;
            const bg = ["bg-cyan-500", "bg-fuchsia-500", "bg-amber-500", "bg-violet-500"][i % 4];
            return (
              <button
                key={o.id}
                data-testid={`play-option-${o.id}`}
                onClick={() => onSubmit(o.id)}
                disabled={answered || submitting}
                className={`w-full py-4 rounded-xl text-white font-display font-bold text-left px-4 flex items-center gap-3 transition ${bg} ${answered && !isMine ? "opacity-30" : "hover:brightness-110 active:scale-95"} ${isMine ? "ring-4 ring-white" : ""}`}
              >
                <span className="text-[10px] font-mono-stat opacity-70">{o.id.toUpperCase()}.</span>
                <span>{o.text}</span>
              </button>
            );
          })}
        </div>
        {answered && <div data-testid="play-answered-badge" className="text-center text-cyan-300 font-mono-stat text-sm mt-2">Réponse enregistrée</div>}
      </div>
    );
  }
  if (phase === "ranking_intermediate") {
    const my = state.my_answer;
    return (
      <div data-testid="play-ranking-intermediate" className="flex-1 flex flex-col items-center justify-center gap-3 text-center">
        {my && (
          <div className={`px-6 py-3 rounded-2xl text-3xl font-display font-black ${my.is_correct ? "bg-emerald-500 text-black" : "bg-rose-500 text-white"}`}>
            {my.is_correct ? "✔ Bonne réponse !" : "✘ Mauvaise réponse"}
          </div>
        )}
        {my && <div data-testid="play-question-score" className="text-lg font-mono-stat text-white/70">+{my.score || 0} points</div>}
        <div className="text-4xl font-display font-black" data-testid="play-rank">Rang #{state.my_rank || "-"}</div>
        <div className="text-white/70 font-mono-stat">Score {state.my_score || 0} pts</div>
        {remaining !== null && <div className="mt-3 text-fuchsia-300 font-mono-stat text-sm">Prochaine question dans {remaining}s</div>}
      </div>
    );
  }
  if (phase === "quiz_final_ranking") {
    return (
      <div data-testid="play-quiz-final" className="flex-1 flex flex-col items-center gap-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono-stat">Classement final — {state.current_quiz?.title}</div>
        <div className="text-4xl font-display font-black">#{state.my_rank || "-"}</div>
        <div className="text-white/70 font-mono-stat">Score {state.my_score || 0} pts</div>
        {typeof state.current_quiz?.prize_pool_amount === "number" && (
          <div className="text-lg text-amber-300 font-mono-stat">Cagnotte : {formatEuros(state.current_quiz.prize_pool_amount)}</div>
        )}
        <LeaderboardList rows={state.leaderboard || []} data-testid="play-leaderboard" />
        <div className="text-white/40 text-xs mt-3">En attente du prochain quiz…</div>
      </div>
    );
  }
  if (phase === "event_recap" || phase === "completed") {
    return (
      <div data-testid="play-recap" className="flex-1 flex flex-col gap-4 pb-6">
        <div className="text-center">
          <Trophy className="w-10 h-10 text-amber-300 mx-auto"/>
          <div className="font-display font-black text-2xl mt-2">Récapitulatif de la soirée</div>
        </div>
        {(state.recap || []).map((r, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-3">
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-mono-stat">Quiz {i + 1}</div>
            <div className="font-display font-bold">{r.title}</div>
            <LeaderboardList rows={(r.leaderboard || []).slice(0, 5)} />
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function LeaderboardList({ rows, ...rest }) {
  return (
    <div className="w-full space-y-1 mt-2" {...rest}>
      {rows.map((r, i) => (
        <div key={r.participant_id || i} data-testid={`play-lb-${i + 1}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono-stat text-white/40 w-6">#{r.rank}</span>
            <span>{r.nickname}</span>
          </div>
          <span className="font-mono-stat text-cyan-300">{r.score} pts</span>
        </div>
      ))}
    </div>
  );
}

/* ============================================================================
   PUBLIC — Giant Screen
   Route: /live/:code/screen
============================================================================ */
export function LiveScreen() {
  const { code } = useParams();
  const [state, setState] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/${code}/screen-state`);
        if (!cancelled) setState(data);
      } catch (_) { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 1500);
    return () => { cancelled = true; clearInterval(t); };
  }, [code]);

  if (!state) return <div className="min-h-screen bg-black flex items-center justify-center text-white/50">Chargement…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-fuchsia-950/40 to-cyan-950/40 flex flex-col p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="font-display font-black text-4xl inline-flex items-center gap-3">
          <Sparkles className="w-10 h-10 text-fuchsia-400"/> OTRAQUIZZ
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono-stat">Code</div>
          <div data-testid="screen-code" className="font-mono-stat text-4xl text-cyan-300 tracking-widest">#{state.code}</div>
          <div data-testid="screen-participants" className="text-xs text-white/50 mt-1 font-mono-stat inline-flex items-center gap-1"><Users className="w-3 h-3"/> {state.participants_count} joueurs</div>
        </div>
      </div>
      <ScreenContent state={state} />
    </div>
  );
}

function ScreenContent({ state }) {
  const remaining = useCountdown(state.paused ? null : state.phase_deadline, state.paused);
  const phase = state.phase;
  if (phase === "waiting") {
    return (
      <div data-testid="screen-waiting" className="flex-1 flex flex-col items-center justify-center text-center gap-6">
        <Radio className="w-24 h-24 text-fuchsia-400 animate-pulse"/>
        <div className="font-display font-black text-6xl">{state.title}</div>
        <div className="text-white/80 text-3xl">L&apos;événement va bientôt commencer</div>
        <div className="text-white/60 text-xl mt-4">Rejoignez la soirée : <span className="text-cyan-300 font-mono-stat">otraq.uiz/live</span> · Code <span className="text-cyan-300 font-mono-stat">#{state.code}</span></div>
      </div>
    );
  }
  if (phase === "quiz_intro" && state.current_quiz) {
    return (
      <div data-testid="screen-quiz-intro" className="flex-1 flex flex-col items-center justify-center text-center gap-4">
        <div className="text-2xl uppercase tracking-widest text-fuchsia-300 font-mono-stat">Quiz {state.current_quiz_index + 1} / {state.quizzes_count}</div>
        <div className="font-display font-black text-7xl">{state.current_quiz.title}</div>
        {state.current_quiz.description && <div className="text-2xl text-white/70">{state.current_quiz.description}</div>}
        <div className="mt-4 text-3xl font-mono-stat text-white/80">{state.current_quiz.questions_count} questions</div>
        {typeof state.current_quiz.prize_pool_amount === "number" && (
          <div className="text-4xl text-amber-300 font-mono-stat mt-2">Cagnotte : {formatEuros(state.current_quiz.prize_pool_amount)}</div>
        )}
        <div className="mt-6 text-fuchsia-300 uppercase font-mono-stat tracking-widest text-2xl">Préparez-vous</div>
      </div>
    );
  }
  if (["question_active", "ranking_intermediate"].includes(phase) && state.current_question) {
    const q = state.current_question;
    const isRanking = phase === "ranking_intermediate";
    return (
      <div className="flex-1 flex flex-col gap-6">
        <div className="flex items-center justify-between text-2xl font-mono-stat text-white/60">
          <span>Question {state.current_question_index + 1} / {state.current_quiz?.questions_count}</span>
          {remaining !== null && <span className="text-amber-300 text-5xl inline-flex items-center gap-2"><Timer className="w-8 h-8"/> {remaining}s</span>}
        </div>
        <div data-testid="screen-question-text" className="font-display font-black text-5xl text-center py-4">{q.text}</div>
        <div className="grid grid-cols-2 gap-4">
          {(q.options || []).map((o, i) => {
            const isCorrect = isRanking && o.id === q.correct_option_id;
            const bg = isCorrect ? "bg-emerald-500 text-black border-emerald-300" : ["bg-cyan-500/20 border-cyan-400", "bg-fuchsia-500/20 border-fuchsia-400", "bg-amber-500/20 border-amber-400", "bg-violet-500/20 border-violet-400"][i % 4];
            return (
              <div key={o.id} data-testid={`screen-option-${o.id}`}
                className={`rounded-2xl border-2 px-6 py-6 font-display font-bold text-3xl ${bg}`}>
                <span className="text-xl opacity-60 mr-3">{o.id.toUpperCase()}.</span>
                {o.text}
              </div>
            );
          })}
        </div>
        {isRanking && (
          <div className="mt-2">
            <div className="text-lg uppercase tracking-widest text-emerald-300 font-mono-stat mb-2">Classement intermédiaire</div>
            <ScreenLeaderboard rows={state.leaderboard || []} />
          </div>
        )}
      </div>
    );
  }
  if (phase === "quiz_final_ranking") {
    const rows = state.leaderboard || [];
    return (
      <div data-testid="screen-quiz-final" className="flex-1 flex flex-col gap-6">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-amber-300 mx-auto"/>
          <div className="font-display font-black text-6xl mt-3">Classement final</div>
          <div className="text-2xl text-white/70">{state.current_quiz?.title}</div>
          {typeof state.current_quiz?.prize_pool_amount === "number" && (
            <div className="text-3xl text-amber-300 font-mono-stat mt-3">Cagnotte : {formatEuros(state.current_quiz.prize_pool_amount)}</div>
          )}
        </div>
        <ScreenPodium rows={rows} />
        <ScreenLeaderboard rows={rows.slice(3, 10)} startRank={4} />
      </div>
    );
  }
  if (phase === "event_recap" || phase === "completed") {
    return (
      <div data-testid="screen-recap" className="flex-1 flex flex-col gap-4 overflow-y-auto">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-amber-300 mx-auto"/>
          <div className="font-display font-black text-5xl mt-2">Récapitulatif de la soirée</div>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          {(state.recap || []).map((r, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-black/40 p-4">
              <div className="text-sm uppercase tracking-widest text-fuchsia-300 font-mono-stat">Quiz {i + 1}</div>
              <div className="font-display font-bold text-xl">{r.title}</div>
              {typeof r.prize_pool_amount === "number" && <div className="text-amber-300 text-sm font-mono-stat">{formatEuros(r.prize_pool_amount)}</div>}
              <ScreenLeaderboard rows={(r.leaderboard || []).slice(0, 5)} compact />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

function ScreenPodium({ rows }) {
  const top3 = rows.slice(0, 3);
  return (
    <div data-testid="screen-podium" className="grid grid-cols-3 gap-4 items-end">
      {top3.map((r, i) => {
        const rank = r.rank || i + 1;
        const height = rank === 1 ? "h-40" : rank === 2 ? "h-32" : "h-24";
        const bg = rank === 1 ? "bg-amber-400 text-black" : rank === 2 ? "bg-white/70 text-black" : "bg-orange-500/60 text-black";
        return (
          <div key={r.participant_id || i} data-testid={`screen-podium-${rank}`} className="flex flex-col items-center gap-2">
            {rank === 1 && <Crown className="w-8 h-8 text-amber-300"/>}
            <div className="font-display font-black text-2xl">{r.nickname}</div>
            <div className="font-mono-stat text-cyan-300">{r.score} pts</div>
            <div className={`w-full rounded-t-xl ${height} ${bg} flex items-start justify-center font-display font-black text-4xl pt-3`}>#{rank}</div>
          </div>
        );
      })}
    </div>
  );
}

function ScreenLeaderboard({ rows, startRank = 1, compact = false }) {
  return (
    <div className={`space-y-1 ${compact ? "text-sm" : "text-lg"}`}>
      {rows.map((r, i) => (
        <div key={r.participant_id || i} data-testid={`screen-lb-${(r.rank || startRank + i)}`}
          className="flex items-center justify-between px-4 py-2 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center gap-3">
            <span className="font-mono-stat text-white/40 w-8">#{r.rank || startRank + i}</span>
            <span className="font-display font-bold">{r.nickname}</span>
          </div>
          <span className="font-mono-stat text-cyan-300">{r.score} pts</span>
        </div>
      ))}
    </div>
  );
}
